import { useId, useState } from "react";

import { Badge } from "../../../../core/Badge.js";
import { buttonStyles } from "../../../../core/Button.styles.js";
import { ChipRadioGroup } from "../../../../core/Chip.js";
import { EmptyState } from "../../../../core/EmptyState.js";
import { LoadMore } from "../../../../core/LoadMore.js";
import { Table, type TableColumn } from "../../../../core/Table.js";
import { Text } from "../../../../core/Text.js";
import { Amount } from "../../../../domain/Amount.js";
import { ItemArt } from "../../../../domain/ItemArt.js";
import { AppLink } from "../../../../foundations/AppLink.js";
import { ActionButton } from "../../../shared/ActionButton.js";
import type { TemplateAction } from "../../../shared/content.js";
import { LoadError } from "../../../shared/LoadError.js";
import type {
  AccountHistoryContent,
  BattleHistoryRow,
  HistoryPaging,
  HistoryState,
  OpenHistoryRow,
} from "../../accountContent.js";

type View = "opens" | "battles";

/**
 * History: the shopper's opens, each linked to its verification, and the
 * battles they hold a seat in. A store with battles switches between the two lists with chips;
 * which list shows is the only state this section keeps.
 */
export function AccountHistory({
  title,
  currency,
  viewLabel,
  opens,
  battles,
  footnote,
}: AccountHistoryContent) {
  const id = useId();
  const [view, setView] = useState<View>("opens");
  const showing = battles && view === "battles" ? "battles" : "opens";
  return (
    <section aria-labelledby={id} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Text as="h2" id={id} variant="heading-h1" tone="primary">
          {title}
        </Text>
        {battles && (
          <ChipRadioGroup
            label={viewLabel}
            value={showing}
            onValueChange={setView}
            options={[
              { value: "opens", label: opens.label },
              { value: "battles", label: battles.label },
            ]}
          />
        )}
      </div>
      {showing === "opens" ? (
        <HistoryList
          caption={opens.label}
          state={opens.state}
          paging={opens.paging}
          columns={openColumns(opens.columns, currency)}
        />
      ) : (
        battles && (
          <HistoryList
            caption={battles.label}
            state={battles.state}
            paging={battles.paging}
            columns={battleColumns(battles, currency)}
          />
        )
      )}
      {footnote && (
        <Text variant="body-b2" tone="tertiary">
          {footnote}
        </Text>
      )}
    </section>
  );
}

function HistoryList<Row extends { id: string }>({
  caption,
  state,
  paging,
  columns,
}: {
  caption: string;
  state: HistoryState<Row>;
  paging: HistoryPaging;
  columns: TableColumn<Row>[];
}) {
  if (state.status === "error") return <LoadError message={state.message} />;
  if (state.status === "empty") {
    return (
      <EmptyState
        title={state.message}
        action={state.action && <ActionButton action={state.action} variant="secondary" />}
      />
    );
  }
  const rows = state.status === "ready" ? state.rows : [];
  return (
    <Table
      caption={caption}
      hideCaption
      density="comfortable"
      loading={state.status === "loading"}
      rows={rows}
      rowKey={(row) => row.id}
      columns={columns}
      footer={
        paging.hasMore || rows.length > 0 ? (
          <LoadMore
            shown={rows.length}
            hasMore={paging.hasMore}
            loading={paging.loading}
            onLoadMore={paging.onLoadMore}
            label={paging.label}
            status={(shown) => paging.status(shown)}
          />
        ) : undefined
      }
    />
  );
}

function RowLink({ action }: { action: TemplateAction }) {
  return (
    <AppLink href={action.href} className={buttonStyles({ variant: "ghost", size: "sm" })}>
      {action.label}
    </AppLink>
  );
}

function Thumb({ src, name }: { src: string; name: string }) {
  return (
    <span className="size-8 shrink-0 overflow-hidden rounded-md bg-bg-raised" title={name}>
      {src ? <ItemArt src={src} alt="" shape="square" className="size-full" /> : null}
    </span>
  );
}

function openColumns(
  columns: AccountHistoryContent["opens"]["columns"],
  currency: AccountHistoryContent["currency"],
): TableColumn<OpenHistoryRow>[] {
  return [
    {
      key: "item",
      header: columns.item,
      rowHeader: true,
      cell: (row) => (
        <span className="flex items-center gap-3">
          <Thumb src={row.item.imageUrl} name={row.item.name} />
          <span className="font-body text-body-b2-strong text-text-primary">{row.item.name}</span>
        </span>
      ),
    },
    {
      key: "value",
      header: columns.value,
      numeric: true,
      nowrap: true,
      cell: (row) => <Amount cents={row.valueCents} currency={currency} size="meta" />,
    },
    {
      key: "cost",
      header: columns.cost,
      numeric: true,
      nowrap: true,
      hideBelow: "sm",
      cell: (row) => <Amount cents={row.costCents} currency={currency} size="meta" />,
    },
    { key: "when", header: columns.when, nowrap: true, hideBelow: "md", cell: (row) => row.when },
    {
      key: "verify",
      header: columns.verify,
      align: "end",
      cell: (row) => <RowLink action={row.verify} />,
    },
  ];
}

function battleColumns(
  battles: NonNullable<AccountHistoryContent["battles"]>,
  currency: AccountHistoryContent["currency"],
): TableColumn<BattleHistoryRow>[] {
  const { columns } = battles;
  return [
    {
      key: "battle",
      header: columns.battle,
      rowHeader: true,
      cell: (row) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1">
            {row.rounds.map((round, index) => (
              // Rounds repeat a box, so the index keeps each its own.
              // biome-ignore lint/suspicious/noArrayIndexKey: rounds have no id of their own.
              <Thumb key={index} src={round.imageUrl} name={round.name} />
            ))}
            {row.moreRounds > 0 && (
              <span className="font-body text-body-b3 text-text-tertiary">+{row.moreRounds}</span>
            )}
          </span>
          <span title={row.mode.rule}>
            <Badge tone={row.mode.tone}>{row.mode.label}</Badge>
          </span>
          <span className="font-body text-body-b3 text-text-tertiary">{row.summary}</span>
        </span>
      ),
    },
    {
      key: "result",
      header: columns.result,
      cell: (row) => <Badge tone={row.result.tone}>{row.result.label}</Badge>,
    },
    {
      key: "pot",
      header: columns.pot,
      numeric: true,
      nowrap: true,
      hideBelow: "sm",
      cell: (row) =>
        row.potCents === null ? (
          battles.pendingPot
        ) : (
          <Amount cents={row.potCents} currency={currency} size="meta" />
        ),
    },
    {
      key: "cost",
      header: columns.cost,
      numeric: true,
      nowrap: true,
      hideBelow: "md",
      cell: (row) => <Amount cents={row.costCents} currency={currency} size="meta" />,
    },
    { key: "when", header: columns.when, nowrap: true, hideBelow: "lg", cell: (row) => row.when },
    {
      key: "link",
      header: columns.link,
      align: "end",
      cell: (row) => <RowLink action={row.link} />,
    },
  ];
}
